import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Upload, Trash2, Loader2, LogOut, Users, Image as ImageIcon, MessageSquare, UserCircle, ArrowLeft } from "lucide-react";
import GiftsAdmin from "@/components/GiftsAdmin";
import { toast } from "sonner";

function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("admin@example.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) toast.error(res.error);
    else if (res.user.role !== "admin") toast.error("Not an admin account");
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-ink grain p-6">
      <form onSubmit={submit} className="w-full max-w-sm animate-fade-up">
        <div className="w-12 h-12 rounded-2xl bg-brand flex items-center justify-center mb-6 brand-glow">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Admin Portal</h1>
        <p className="text-muted-foreground mb-6 text-sm">Restricted access.</p>
        <Input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Email" className="h-13 bg-surface border-white/10 rounded-2xl mb-3 h-14" />
        <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Password" className="bg-surface border-white/10 rounded-2xl mb-4 h-14" />
        <Button data-testid="admin-login-button" disabled={loading}
          className="w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-white font-semibold transition-colors">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

const STAT_ICONS = { users: Users, characters: UserCircle, assets: ImageIcon, messages: MessageSquare };

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState({});
  const [form, setForm] = useState({ character_name: "", origin: "western", gender: "female", category: "portrait", outfit_label: "Casual Apparel" });
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/assets").then(({ data }) => setAssets(data)).catch(() => {});
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (!ready || user === null) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (!user || user.role !== "admin") return <AdminLogin />;

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setB64(String(reader.result).split(",")[1]);
    };
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!b64 || !form.character_name.trim()) { toast.error("Add an image and character name"); return; }
    setUploading(true);
    try {
      await api.post("/admin/assets", { ...form, image_base64: b64 });
      toast.success("Asset uploaded");
      setPreview(null); setB64(null); setForm((f) => ({ ...f, character_name: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  const remove = async (id) => { await api.delete(`/admin/assets/${id}`); load(); toast.success("Deleted"); };

  return (
    <div className="min-h-screen bg-ink grain">
      <div className="glass border-b border-white/10 px-6 py-4 flex items-center gap-3 sticky top-0 z-30">
        <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><Shield className="w-5 h-5 text-white" /></div>
        <h1 className="font-heading text-lg font-bold tracking-tight flex-1">Admin Portal</h1>
        <Button variant="ghost" data-testid="admin-to-app" onClick={() => nav("/app")} className="rounded-full text-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> App
        </Button>
        <Button variant="ghost" data-testid="admin-logout" onClick={() => { logout(); nav("/login"); }} className="rounded-full text-sm text-muted-foreground">
          <LogOut className="w-4 h-4 mr-1" /> Logout
        </Button>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {["users", "characters", "assets", "messages"].map((k) => {
            const Icon = STAT_ICONS[k];
            return (
              <div key={k} className="bg-surface border border-white/10 rounded-2xl p-5" data-testid={`stat-${k}`}>
                <Icon className="w-5 h-5 text-brand mb-3" />
                <p className="font-heading text-3xl font-bold leading-none">{stats[k] ?? 0}</p>
                <p className="block text-xs uppercase tracking-[0.15em] text-muted-foreground capitalize mt-2">{k}</p>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* uploader */}
          <div className="bg-surface border border-white/10 rounded-3xl p-6">
            <h2 className="font-heading text-xl font-bold mb-5">Upload Character Asset</h2>
            <div
              data-testid="drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
              className={`relative rounded-2xl border-2 border-dashed transition-colors aspect-video flex flex-col items-center justify-center overflow-hidden ${drag ? "border-brand bg-brand/5" : "border-white/15"}`}>
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <>
                  <Upload className="w-7 h-7 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Drag & drop or click to browse</p>
                </>
              )}
              <input data-testid="file-input" type="file" accept="image/*"
                onChange={(e) => handleFile(e.target.files[0])}
                className="absolute inset-0 opacity-0 cursor-pointer" />
            </div>

            <div className="space-y-3 mt-5">
              <Input data-testid="asset-name" value={form.character_name}
                onChange={(e) => setForm({ ...form, character_name: e.target.value })}
                placeholder="Character name" className="h-12 bg-ink border-white/10 rounded-xl" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.origin} onValueChange={(v) => setForm({ ...form, origin: v })}>
                  <SelectTrigger data-testid="asset-origin" className="h-12 bg-ink border-white/10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="indian">Indian</SelectItem>
                    <SelectItem value="western">Western / USA</SelectItem>
                    <SelectItem value="eastern_european">Eastern European</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="asset-gender" className="h-12 bg-ink border-white/10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="asset-category" className="h-12 bg-ink border-white/10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="outfit">Outfit / Wardrobe</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={form.outfit_label} onValueChange={(v) => setForm({ ...form, outfit_label: v })}>
                  <SelectTrigger data-testid="asset-outfit" className="h-12 bg-ink border-white/10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Casual Apparel">Casual Apparel</SelectItem>
                    <SelectItem value="Formal Business Uniform">Formal Business</SelectItem>
                    <SelectItem value="Intimate Evening Attire">Evening Attire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="asset-upload-button" onClick={upload} disabled={uploading}
                className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-semibold transition-colors">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Upload Asset</>}
              </Button>
            </div>
          </div>

          {/* asset library */}
          <div className="bg-surface border border-white/10 rounded-3xl p-6">
            <h2 className="font-heading text-xl font-bold mb-5">Asset Library ({assets.length})</h2>
            {assets.length === 0 ? (
              <p className="text-sm text-muted-foreground">No assets uploaded yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[560px] overflow-y-auto">
                {assets.map((a) => (
                  <div key={a.id} data-testid={`asset-${a.id}`} className="relative rounded-2xl overflow-hidden border border-white/10 group aspect-[3/4]">
                    <img src={`data:image/jpeg;base64,${a.image_base64}`} alt={a.character_name} className="w-full h-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2">
                      <p className="text-xs font-semibold truncate">{a.character_name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{a.origin} • {a.category === "outfit" ? a.outfit_label : "portrait"}</p>
                    </div>
                    <button data-testid={`delete-asset-${a.id}`} onClick={() => remove(a.id)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <GiftsAdmin />
      </div>
    </div>
  );
}
